import type { ApiClient } from '@momonos/api-client'
import { formatBdt } from '@momonos/shared'
import { Badge, Button, Card, Loader, useToast } from '@momonos/ui'
import { useCallback, useEffect, useState } from 'react'

/** FR-ADM-1 / FR-CAT-5 — the menu, and the 86 switch. */
interface AdminProduct {
  id: string
  nameEn: string
  nameBn: string
  price: number
  isAvailable: boolean
  isBestseller: boolean
  isVeg: boolean
  categoryId: string
}

export function MenuScreen({ api, branchId }: { api: ApiClient; branchId: string | null }) {
  const { show } = useToast()
  const [products, setProducts] = useState<AdminProduct[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    const result = await api.get<{ products: AdminProduct[] }>('/v1/admin/products', {
      query: { branch: branchId ?? undefined },
    })
    setProducts(result.products)
  }, [api, branchId])

  useEffect(() => {
    void load().catch(() => show('Could not load the menu', { tone: 'error' }))
  }, [load, show])

  async function toggle(product: AdminProduct) {
    setBusyId(product.id)
    // Optimistic: a manager 86ing an item mid-service needs it to feel instant.
    setProducts(
      (current) =>
        current?.map((entry) =>
          entry.id === product.id ? { ...entry, isAvailable: !entry.isAvailable } : entry,
        ) ?? null,
    )

    try {
      await api.patch(`/v1/admin/products/${product.id}/availability`, {
        isAvailable: !product.isAvailable,
      })
      show(
        product.isAvailable ? `${product.nameEn} marked sold out` : `${product.nameEn} is back on`,
        { tone: 'success' },
      )
    } catch {
      show('Could not update availability', { tone: 'error' })
      await load()
    } finally {
      setBusyId(null)
    }
  }

  if (!products) return <Loader label="Loading the menu" />

  return (
    <div className="space-y-4">
      <h1 className="font-bold text-2xl text-content">Menu</h1>

      <div className="grid gap-3">
        {products.map((product) => (
          <Card key={product.id} className="flex items-center gap-4">
            <div className="flex-1">
              <p className="font-medium text-content">
                {product.nameEn}
                <span className="ml-2 text-content-muted" lang="bn">
                  {product.nameBn}
                </span>
              </p>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-content-secondary tabular">{formatBdt(product.price)}</span>
                {product.isBestseller && <Badge tone="yellow">Bestseller</Badge>}
                {product.isVeg && <Badge tone="green">Veg</Badge>}
                {!product.isAvailable && <Badge tone="red">Sold out</Badge>}
              </div>
            </div>

            <Button
              variant={product.isAvailable ? 'secondary' : 'primary'}
              isLoading={busyId === product.id}
              onClick={() => toggle(product)}
            >
              {product.isAvailable ? 'Mark sold out' : 'Back on menu'}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  )
}
